@if (!this.data.supplier_implementation) {
  <div class="container">
    <div class="row mt-3">
      <div class="col">
        <em>No implementation loaded.</em>
      </div>
    </div>
  </div>
} @else {
  <div class="container-fluid">
    <div class="row page-header bg-light py-2 border-bottom">
      <div class="col-auto">
        <button class="btn btn-outline-danger"
          [disabled]="totalCount() === 0"
          (click)="deleteAllSelectors()">
          Delete all selectors
        </button>
      </div>
      <div class="col-auto align-self-center">
        <div id="status"></div>
      </div>
      <div class="col-auto align-self-center text-muted">
        {{ sections.length }} fields, {{ totalCount() }} steps
      </div>
    </div>
    <div class="row test-bar bg-white py-2 border-bottom">
      <div class="col-12">
        <label for="html-input" class="form-label mb-1">Test against pasted HTML</label>
        <textarea id="html-input"
          class="form-control form-control-sm html-input"
          rows="4"
          placeholder="Paste full page HTML here..."
        [(ngModel)]="htmlInput"></textarea>
      </div>
      <div class="col-12 mt-2 d-flex align-items-center gap-2">
        <button class="btn btn-primary btn-sm"
          [disabled]="totalCount() === 0 || !htmlInput"
          (click)="runTest()">
          Run test
        </button>
        <button class="btn btn-outline-secondary btn-sm" (click)="clearTestResults()">
          Clear results
        </button>
        <span class="text-muted small">{{ testStatus }}</span>
      </div>
    </div>
    <div class="row">
      <aside class="col-auto quick-nav">
        <ul class="list-unstyled">
          @for (s of sections; track s) {
            <li>
              <a href="javascript:void(0)" (click)="scrollToAnchor(s.anchor)">{{ s.label }} ({{ s.getList().length }})</a>
            </li>
          }
        </ul>
      </aside>
      <main class="col sections">
        @for (s of sections; track s) {
          <section [id]="s.anchor" class="mb-4">
            <div class="d-flex align-items-center gap-2">
              <h3 class="mb-0">{{ s.label }}</h3>
              <button type="button" class="btn btn-outline-secondary btn-sm" (click)="addSchemaStep(s)"
                title="Reads the value from the page's schema.org data (JSON-LD, microdata, OpenGraph)">
                + Schema step
              </button>
              @if (s.getList().length > 0) {
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  (click)="deleteAllInSection(s)">
                  Delete all ({{ s.getList().length }})
                </button>
              }
            </div>
            <div class="text-muted small mb-1">
              Extraction tries the steps top to bottom; the first one that yields a value wins.
            </div>
            @if (staticValue(s); as staticValue) {
              <div class="chain-static">
                <strong>Static value</strong> overrides every step: <code>{{ staticValue }}</code>
              </div>
            }
            @if (s.getList().length > 0) {
              <table class="table table-sm table-striped selectors-table" [class.opacity-50]="staticValue(s)">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Extracted During Selector Search</th>
                    <th>Extracted on Test</th>
                    <th>Selector / schema keys</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @for (sel of ordered(s); track sel; let i = $index; let first = $first; let last = $last) {
                    <tr [class.step-disabled]="isDisabled(sel)">
                      <td>{{ i + 1 }}</td>
                      <td>
                        <span class="step-kind" [class.step-kind-schema]="sel.is_schema">{{ kind(sel) }}</span>
                        @if (isDisabled(sel)) {
                          <div class="small text-muted">off</div>
                        }
                      </td>
                      <td>{{ sel.extracted_value }}</td>
                      <td>
                        @if (getTestResult(sel); as tr) {
                          @if (!tr.error) {
                            <span class="test-ok">{{ tr.extracted_value }}</span>
                          }
                          @if (tr.error) {
                            <span class="test-err">ERROR: {{ tr.error }}</span>
                          }
                        } @else {
                          <span class="text-muted">—</span>
                        }
                      </td>
                      <td>
                        @if (sel.is_schema) {
                          <input type="text"
                            class="form-control form-control-sm selector-path"
                            placeholder="default lookup for this field"
                            title="Alternative key paths, separated by ','; nested keys by '.' (e.g. offers.price, price). Empty = default."
                            [ngModel]="schemaPathsText(sel)"
                            (ngModelChange)="onSchemaPathsChange(sel, $event)">
                        } @else {
                          @for (path of visiblePaths(sel); track path) {
                            <div class="selector-path">{{ path }}</div>
                          }
                          @if (sel.selector.length > collapsedPathLimit) {
                            <button
                              type="button"
                              class="btn btn-link btn-sm p-0"
                              (click)="togglePathsExpanded(sel)">
                              {{ isPathsExpanded(sel) ? 'Show less' : 'Show ' + (sel.selector.length - collapsedPathLimit) + ' more' }}
                            </button>
                          }
                        }
                      </td>
                      <td>
                        <div class="d-flex gap-1 mb-1">
                          <button type="button" class="btn btn-outline-secondary btn-sm" [disabled]="first"
                            title="Try earlier" (click)="move(s, sel, -1)">↑</button>
                          <button type="button" class="btn btn-outline-secondary btn-sm" [disabled]="last"
                            title="Try later" (click)="move(s, sel, 1)">↓</button>
                        </div>
                        <input type="number"
                          min="-1000"
                          max="1000"
                          step="1"
                          class="form-control form-control-sm priority-input"
                          title="Manual priority: larger runs earlier"
                          [ngModel]="sel.manual_priority"
                          (ngModelChange)="onPriorityChange(sel, $event)">
                      </td>
                      <td>
                        <div class="d-flex flex-column gap-1">
                          <label class="small d-flex align-items-center gap-1">
                            <input type="checkbox" [ngModel]="!isDisabled(sel)" (ngModelChange)="setEnabled(sel, $event)">
                            on
                          </label>
                          <button class="btn btn-danger btn-sm" (click)="deleteSelector(s, sel)">Delete</button>
                          <button
                            type="button"
                            class="btn btn-sm"
                            [class.btn-outline-secondary]="!isPPExpanded(sel)"
                            [class.btn-secondary]="isPPExpanded(sel)"
                            (click)="togglePP(sel)">
                            PP{{ ppStepCount(sel) > 0 ? ' (' + ppStepCount(sel) + ')' : '' }}
                          </button>
                        </div>
                      </td>
                    </tr>
                    @if (isPPExpanded(sel)) {
                      <tr class="pp-row">
                        <td colspan="7">
                          <app-post-processing-editor
                            [sel]="sel"
                            [field]="s.field"
                            [pageSource]="data.supplier_implementation.direct_hits[0].page_source"
                            [pageSource2]="htmlInput"
                            [token]="data.token">
                          </app-post-processing-editor>
                        </td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            } @else {
              <em class="text-muted">no steps</em>
            }
            @if (s.staticValueKey) {
              <div class="chain-fallback d-flex align-items-center gap-2">
                <label class="mb-0 text-nowrap" [for]="s.anchor + '-fallback'"><strong>Fallback value</strong></label>
                <input type="text" class="form-control form-control-sm w-50"
                  [id]="s.anchor + '-fallback'"
                  placeholder="used when no step yields a value"
                  [ngModel]="fallbackValues[s.staticValueKey]"
                  (ngModelChange)="setFallbackValue(s, $event)">
              </div>
            }
          </section>
        }
      </main>
    </div>
  </div>
}

